import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type {
  CreateProfileInput,
  MeasurementProfile,
  ProfileRevision,
  UpdateProfileInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  createProfile,
  deleteProfile,
  getCaptureForm,
  listProfiles,
  profileHistory,
  updateProfile,
  type CaptureForm,
  type MeasurementOwner,
} from './profiles-api.js';

export type { MeasurementOwner };

/**
 * Everything sits under `all`, and that is the whole invalidation strategy: the three mutations
 * below already blow away `all` wholesale, so every customer's list refreshes without any of them
 * being named.
 */
export const profileKeys = {
  all: ['measurement-profiles'] as const,
  forCustomer: (customerId: string) => ['measurement-profiles', 'customer', customerId] as const,
  forOwner: (owner: MeasurementOwner) =>
    ['measurement-profiles', 'customer', owner.customerId] as const,
  form: (garmentTypeKey: string, profileId?: string) =>
    ['measurement-profiles', 'form', garmentTypeKey, profileId ?? null] as const,
  history: (profileId: string) => ['measurement-profiles', 'history', profileId] as const,
};

export function useOwnerProfiles(
  owner: MeasurementOwner | null,
): UseQueryResult<MeasurementProfile[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: owner ? profileKeys.forOwner(owner) : profileKeys.all,
    queryFn: () => listProfiles(client, owner as MeasurementOwner),
    enabled: owner !== null,
  });
}

export function useCustomerProfiles(
  customerId: string | null,
): UseQueryResult<MeasurementProfile[]> {
  return useOwnerProfiles(customerId === null ? null : { kind: 'customer', customerId });
}

/** The form to render, fetched rather than built — see `getCaptureForm`. */
export function useCaptureForm(
  garmentTypeKey: string | null,
  profileId?: string,
): UseQueryResult<CaptureForm> {
  const client = useApiClient();
  return useQuery({
    queryKey: profileKeys.form(garmentTypeKey ?? '', profileId),
    queryFn: () => getCaptureForm(client, garmentTypeKey as string, profileId),
    enabled: garmentTypeKey !== null,
  });
}

export function useProfileHistory(profileId: string | null): UseQueryResult<ProfileRevision[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: profileKeys.history(profileId ?? ''),
    queryFn: () => profileHistory(client, profileId as string),
    enabled: profileId !== null,
  });
}

export function useCreateProfile() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MeasurementProfile, unknown, CreateProfileInput>({
    mutationFn: (input) => createProfile(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useUpdateProfile(profileId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<MeasurementProfile, unknown, UpdateProfileInput>({
    mutationFn: (patch) => updateProfile(client, profileId, patch),
    onSuccess: () => {
      // The history is a second view of the same change, so it goes stale at the same moment.
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}

export function useDeleteProfile() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (profileId) => deleteProfile(client, profileId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.all });
    },
  });
}
